import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import { selectAccessToken, sessionCleared, sessionEstablished } from './sessionSlice'

import { roleLabelOf } from '@/shared/constants/roles'
import { withMocks } from '@/shared/lib/mockBaseQuery'
import type { ApiEnvelope, SessionApi } from '@/shared/types/apiContract.types'

/**
 * UN SOLO `createApi` para toda la app (D-12).
 *
 * Cada feature agrega los suyos con `baseApi.injectEndpoints`. Si cada feature
 * crea su propio createApi se pierde la invalidación cruzada por tags
 * —eliminar una requisición deja de refrescar el dashboard— y aparecen tantas
 * cachés como features.
 *
 * Los hooks se GENERAN (`useGetRequisitionsQuery`); no se escriben a mano ni se
 * envuelven en un hook propio salvo que agregue lógica real.
 */
const rawBaseQuery = withMocks(
  fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL ?? '/api/v1',
    /**
     * El Bearer es el ACCESS TOKEN de la API (`POST /auth/session`), no el
     * idToken de Firebase: el guard global de `apps/api` solo valida el
     * suyo. El idToken se usa una vez, al canjear la sesión en el login.
     */
    prepareHeaders: (headers, { getState }) => {
      const token = selectAccessToken(getState() as Parameters<typeof selectAccessToken>[0])
      if (token) headers.set('authorization', `Bearer ${token}`)
      return headers
    },
  }),
)

type RawResult = Awaited<ReturnType<typeof rawBaseQuery>>
type QueryApi = Parameters<typeof rawBaseQuery>[1]
type ExtraOptions = Parameters<typeof rawBaseQuery>[2]

const REFRESH_URL = '/auth/refresh'

/**
 * Cada refresh quema la cookie anterior, y el servidor toma una cookie
 * repetida como robo y cierra TODAS las sesiones. Por eso va de uno en uno:
 * en la pestaña (las consultas que caen con 401 a la vez, y el refresh de
 * `RequireSession` al recargar) y entre pestañas, con `navigator.locks`.
 */
let refreshInFlight: Promise<RawResult> | null = null

function withCrossTabLock<T>(run: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !('locks' in navigator)) return run()
  return navigator.locks.request('oranje-auth-refresh', run)
}

function refreshOnce(api: QueryApi, extraOptions: ExtraOptions): Promise<RawResult> {
  // Con su propia señal: si la consulta que lo pidió se cancela, el refresh
  // sigue. Cortado a medias, el servidor ya rotó y la cookie nueva se pierde.
  const detached = { ...api, signal: new AbortController().signal }
  refreshInFlight ??= withCrossTabLock(() =>
    Promise.resolve(
      rawBaseQuery(
        { url: REFRESH_URL, method: 'POST', credentials: 'include' },
        detached,
        extraOptions,
      ),
    ),
  ).finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/** `expired`: el servidor dijo que la sesión murió. `unreachable`: no contestó bien, la sesión sigue. */
type ReauthOutcome = 'renewed' | 'expired' | 'unreachable'

let reauthInFlight: Promise<ReauthOutcome> | null = null

/**
 * Reauth transparente: un 401 intenta UNA vez el refresh con la cookie y
 * reintenta la consulta original. Sin esto, a los 15 minutos toda página
 * nueva moría hasta recargar el navegador (que era lo único que refrescaba).
 * Solo un 401 del refresh manda al login: una falla de red o un 5xx no
 * prueban que la sesión murió.
 */
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const url = typeof args === 'string' ? args : args.url

  // El refresh que pide `RequireSession` pasa por el mismo candado.
  if (url === REFRESH_URL) return refreshOnce(api, extraOptions)

  let result = await rawBaseQuery(args, api, extraOptions)

  const isAuthCall = url.startsWith('/auth/')

  if (result.error?.status === 401 && !isAuthCall) {
    reauthInFlight ??= refreshOnce(api, extraOptions)
      .then((refresh): ReauthOutcome => {
        if (refresh.error) return refresh.error.status === 401 ? 'expired' : 'unreachable'
        const session = (refresh.data as ApiEnvelope<SessionApi>).data
        const role = roleLabelOf(session.user.roleCode)
        api.dispatch(
          sessionEstablished({
            user: {
              id: session.user.id,
              name: session.user.fullName,
              shortName: session.user.fullName,
              email: session.user.email,
              roleId: session.user.roleCode,
              photoUrl: null,
              roleCode: role.short,
              roleTitle: role.title,
              hotel: null,
              department: null,
              zones: [],
              locale: 'es',
              permissions: [],
            },
            accessToken: session.accessToken,
          }),
        )
        return 'renewed'
      })
      .finally(() => {
        reauthInFlight = null
      })

    const outcome = await reauthInFlight
    if (outcome === 'renewed') {
      result = await rawBaseQuery(args, api, extraOptions)
    } else if (outcome === 'expired') {
      api.dispatch(sessionCleared())
    }
  }

  return result
}

export const baseApi = createApi({
  reducerPath: 'api',
  /**
   * `withMocks` es un envoltorio temporal: con `VITE_USE_MOCKS` apagado
   * devuelve este mismo `fetchBaseQuery` sin tocar nada. Los endpoints se
   * declaran siempre con su URL y método definitivos.
   */
  baseQuery: baseQueryWithReauth,
  /** Un tag por entidad del glosario canónico (§8 de Estructura de Proyecto). */
  tagTypes: [
    'Requisition',
    'Position',
    'Slot',
    'Worker',
    'Assignment',
    'Schedule',
    'Timesheet',
    'PunchMark',
    'Prospect',
    'Hotel',
    'Contract',
    'WeeklyConsolidation',
    'Invoice',
    'WorkAccident',
    'BlacklistEntry',
    'Catalog',
    'StaffUser',
    'HotelUser',
    'Audit',
    'ChecklistItem',
    'CorporateEmail',
    'MailSettings',
    'FeatureAccess',
    'RoleGrants',
    'PaymentLimits',
    'PayAdjustment',
    'RecruitmentTeam',
    'HotelTeam',
    'Messaging',
    'HotelDocumentVault',
  ],
  endpoints: () => ({}),
})
